import i18n from "../i18n";
import { DateTime } from "luxon";

enum Weekday {
  Monday = "Monday",
  Tuesday = "Tuesday",
  Wednesday = "Wednesday",
  Thursday = "Thursday",
  Friday = "Friday",
  Saturday = "Saturday",
  Sunday = "Sunday",
}

export type LabeledWeekday = { index: number; value: Weekday; label: string; labelShort: string };
export const getLabeledWeekdays = () => {
  return [
    {
      index: 1,
      value: Weekday.Monday,
      label: i18n.t("weekday-Monday"),
      labelShort: i18n.t("weekday-short-Monday"),
    },
    {
      index: 2,
      value: Weekday.Tuesday,
      label: i18n.t("weekday-Tuesday"),
      labelShort: i18n.t("weekday-short-Tuesday"),
    },
    {
      index: 3,
      value: Weekday.Wednesday,
      label: i18n.t("weekday-Wednesday"),
      labelShort: i18n.t("weekday-short-Wednesday"),
    },
    {
      index: 4,
      value: Weekday.Thursday,
      label: i18n.t("weekday-Thursday"),
      labelShort: i18n.t("weekday-short-Thursday"),
    },
    {
      index: 5,
      value: Weekday.Friday,
      label: i18n.t("weekday-Friday"),
      labelShort: i18n.t("weekday-short-Friday"),
    },
    {
      index: 6,
      value: Weekday.Saturday,
      label: i18n.t("weekday-Saturday"),
      labelShort: i18n.t("weekday-short-Saturday"),
    },
    {
      index: 7,
      value: Weekday.Sunday,
      label: i18n.t("weekday-Sunday"),
      labelShort: i18n.t("weekday-short-Sunday"),
    },
  ];
};

export const getWeekdayLabel = (weekday: Weekday) => {
  const labeledWeekdays = getLabeledWeekdays();
  return labeledWeekdays.find((l) => l.value === weekday)?.label;
};

export const doesWeekdayPreceedCurrentDay = (weekdayIndex: number) => {
  const currentWeekdayIndex = DateTime.now().weekday;
  return weekdayIndex < currentWeekdayIndex;
};

export const getLabeledWeekdayForWeekday = (weekday: Weekday) => {
  return getLabeledWeekdays().find((lw) => lw.value === weekday);
};

export const isWeekdayCurrentDay = (weekdayIndex: number) => {
  const currentWeekdayIndex = DateTime.now().weekday;
  return weekdayIndex === currentWeekdayIndex;
};

export const stringToWeekday = (weekdayString: string) => {
  let weekday = Weekday.Sunday;
  if (Object.values(Weekday).some((w: string) => w === weekdayString)) {
    weekday = weekdayString as Weekday;
  }
  return weekday;
};

export const getShortLabeledWeekdayForWeekdayIndex = (weekdayIndex: number) => {
  const labeledWeekdays = getLabeledWeekdays();
  return labeledWeekdays.find((l) => l.index === weekdayIndex)?.labelShort;
};

// returns -1 if A < B, 0 if A === B, 1 if A > B
export const compareWeekdays = (weekdayA: Weekday, weekdayB: Weekday) => {
  const indexA = getLabeledWeekdays().find((lw) => lw.value === weekdayA)?.index!;
  const indexB = getLabeledWeekdays().find((lw) => lw.value === weekdayB)?.index!;

  if (indexA < indexB) {
    return -1;
  } else if (indexA === indexB) {
    return 0;
  }
  return 1;
};

export const getWeekdayIndexShift = (newWeekday: Weekday, initialWeekday: Weekday) => {
  return (
    getLabeledWeekdays().find((lw) => lw.value === newWeekday)?.index! -
    getLabeledWeekdays().find((lw) => lw.value === initialWeekday)?.index!
  );
};

export const getShiftedWeekday = (weekday: Weekday, index: number) => {
  const weekdayIndex = getLabeledWeekdays().find((lw) => lw.value === weekday)?.index!;
  let shiftedIndex = weekdayIndex + index;
  if (shiftedIndex < 1) {
    shiftedIndex = shiftedIndex + 7;
  } else if (shiftedIndex > 7) {
    shiftedIndex = shiftedIndex - 7;
  }
  return getLabeledWeekdays().find((lw) => lw.index === shiftedIndex)!.value;
};

export const getWeekdayFromIndex = (index: number) => {
  return getLabeledWeekdays().find((lw) => lw.index === index)?.value;
};

export const getIndexFromWeekday = (weekday: Weekday) => {
  return getLabeledWeekdays().find((lw) => lw.value === weekday)?.index!;
};

export default Weekday;